import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { BlogManager } from '../components/blog/BlogManager';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Activity,
  Users,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  LogOut,
  Bell,
  Settings,
  Search,
  Eye,
  Loader2,
  TrendingUp,
  UserCheck,
  FileText,
  AlertTriangle,
  BarChart3,
  Trash2,
  Stethoscope,
  Crown,
  Inbox,
  MessageSquare,
  Phone as PhoneIcon,
  MailOpen,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest, formatDate, getInitials, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, loading: authLoading, login } = useAuth();

  // Helper to build correct document URL from any path format
  const getDocUrl = (path) => {
    if (!path) return null;
    // Full Supabase Storage URL (or any http URL) — return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    // Legacy local upload path → reconstruct via backend
    const filename = path.replace(/^\/?api\/uploads\//, '').replace(/^\/?uploads\//, '');
    return `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${filename}`;
  };
  
  const [analytics, setAnalytics] = useState(null);
  const [providers, setProviders] = useState({ providers: [], labs: [] });
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageCounts, setMessageCounts] = useState({ new: 0, replied: 0, closed: 0, total: 0 });
  const [messagesWarning, setMessagesWarning] = useState(null);
  const [messageFilter, setMessageFilter] = useState('all');
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all'); // all | incomplete | pending | approved | rejected
  const [viewProfile, setViewProfile] = useState(null);
  const [viewUserDetails, setViewUserDetails] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  
  // Admin login form state
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  const needsAdminLogin = !authLoading && (!isAuthenticated || user?.role !== 'admin');

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!adminEmail || !adminPassword) {
      toast.error('Email aur password enter karein');
      return;
    }
    setAdminLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/admin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adminEmail, password: adminPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      
      // Set auth state
      localStorage.setItem('token', data.token);
      window.location.reload();
    } catch (err) {
      toast.error(err.message || 'Invalid email or password');
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      fetchAllData();
    }
  }, [isAuthenticated, user]);

  const fetchWithRetry = async (endpoint, retries = 3) => {
    for (let i = 0; i <= retries; i++) {
      try {
        return await apiRequest(endpoint);
      } catch (err) {
        console.warn(`Attempt ${i + 1} failed for ${endpoint}:`, err.message);
        if (i === retries) throw err;
        await new Promise(r => setTimeout(r, 1500 * (i + 1)));
      }
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    let hasError = false;

    // Fetch each independently so one failure doesn't kill all
    const [analyticsRes, providersRes, appointmentsRes, usersRes, messagesRes] = await Promise.allSettled([
      fetchWithRetry('/admin/analytics'),
      fetchWithRetry('/admin/providers'),
      fetchWithRetry('/admin/appointments'),
      fetchWithRetry('/admin/users'),
      fetchWithRetry('/admin/messages'),
    ]);

    if (analyticsRes.status === 'fulfilled') {
      setAnalytics(analyticsRes.value);
    } else {
      hasError = true;
      setAnalytics({ users: { total: 0, patients: 0, doctors: 0, nurses: 0, labs: 0 }, appointments: { total: 0, pending: 0, completed: 0 }, pending_approvals: 0, revenue: 0 });
    }

    if (providersRes.status === 'fulfilled') {
      setProviders(providersRes.value);
    } else {
      hasError = true;
      setProviders({ providers: [], labs: [] });
    }

    if (appointmentsRes.status === 'fulfilled') {
      setAppointments(appointmentsRes.value.appointments || []);
    } else {
      hasError = true;
      setAppointments([]);
    }

    if (usersRes.status === 'fulfilled') {
      setUsers(usersRes.value.users || []);
    } else {
      hasError = true;
      setUsers([]);
    }

    if (messagesRes.status === 'fulfilled') {
      setMessages(messagesRes.value.messages || []);
      setMessageCounts(messagesRes.value.counts || { new: 0, replied: 0, closed: 0, total: 0 });
      setMessagesWarning(messagesRes.value.warning || null);
    } else {
      setMessages([]);
      setMessagesWarning('Inbox could not load.');
    }


    if (hasError) {
      setError('Some data failed to load.');
      toast.error('Some data failed to load. Click Retry to try again.');
    }
    setLoading(false);
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Kya aap "${userName}" ko delete karna chahte hain? Yeh action undo nahi hoga.`)) return;
    try {
      await apiRequest(`/admin/users/${userId}`, { method: 'DELETE' });
      toast.success('User deleted successfully');
      fetchAllData();
    } catch (error) {
      toast.error(error.message || 'User delete failed');
    }
  };

  const handleApproveProvider = async (providerId, approve) => {
    try {
      await apiRequest(`/admin/providers/${providerId}/approve?approve=${approve}`, {
        method: 'PUT',
      });
      toast.success(approve ? 'Provider approved' : 'Provider rejected');
      fetchAllData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleMessageStatus = async (id, status) => {
    try {
      await apiRequest(`/admin/messages/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      toast.success(`Marked as ${status}`);
      fetchAllData();
    } catch (e) {
      toast.error(e.message || 'Update failed');
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Yeh message permanently delete ho jayega. Continue?')) return;
    try {
      await apiRequest(`/admin/messages/${id}`, { method: 'DELETE' });
      toast.success('Message deleted');
      fetchAllData();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const handleViewUser = async (userId) => {
    setLoadingUserDetails(true);
    try {
      const data = await apiRequest(`/admin/users/${userId}`);
      setViewUserDetails(data);
    } catch (e) {
      toast.error(e.message || 'Could not load user details');
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const pendingProviders = [...(providers.providers || []), ...(providers.labs || [])].filter(
    p => p.approval_status === 'pending'
  );

  const allProviders = [...(providers.providers || []), ...(providers.labs || [])];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F62FE]" />
      </div>
    );
  }

  if (needsAdminLogin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="admin-login">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="w-14 h-14 rounded-xl mx-auto mb-3 bg-[#3B1C73] flex items-center justify-center text-white text-xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>P</div>
            <CardTitle className="text-xl">Admin Panel</CardTitle>
            <p className="text-sm text-slate-500">Authorized access only</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <Label htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="admin@physign.com"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  className="mt-1"
                  data-testid="admin-email-input"
                  required
                />
              </div>
              <div>
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  placeholder="Enter password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="mt-1"
                  data-testid="admin-password-input"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-[#0F62FE]" disabled={adminLoading || !adminEmail || !adminPassword} data-testid="admin-login-btn">
                {adminLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center flex-col gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F62FE]" />
        <p className="text-slate-600">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <span className="text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  Physign
                </span>
              </Link>
              <Badge className="bg-purple-100 text-purple-800">Admin</Badge>
            </div>

            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="w-5 h-5 text-slate-600" />
                {pendingProviders.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                    {pendingProviders.length}
                  </span>
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-sm font-medium text-purple-600">
                      A
                    </div>
                    <span className="hidden sm:block text-sm font-medium">Admin</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Admin Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setActiveTab('settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Admin Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Manage users, providers, and appointments</p>
        </div>

        {/* Error Banner with Retry */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between" data-testid="error-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
              <p className="text-red-700">{error}</p>
            </div>
            <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-100" onClick={fetchAllData} data-testid="retry-btn">
              Retry
            </Button>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="approvals" data-testid="approvals-tab">
              Approvals
              {pendingProviders.length > 0 && (
                <Badge className="ml-2 bg-red-100 text-red-800">{pendingProviders.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="providers" data-testid="providers-tab">Providers</TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="appointments-tab">Appointments</TabsTrigger>
            <TabsTrigger value="blogs" data-testid="blogs-tab">Blogs</TabsTrigger>
            <TabsTrigger value="inbox" data-testid="inbox-tab">
              Inbox
              {messageCounts.new > 0 && (
                <Badge className="ml-2 bg-purple-100 text-purple-800">{messageCounts.new}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Top Row Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">{analytics?.users?.total || 0}</p>
                      <p className="text-sm text-slate-500">Total Users</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">{analytics?.appointments?.total || 0}</p>
                      <p className="text-sm text-slate-500">Total Appointments</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">₹{analytics?.revenue || 0}</p>
                      <p className="text-sm text-slate-500">Total Revenue</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">{(analytics?.users?.doctors || 0) + (analytics?.users?.nurses || 0)}</p>
                      <p className="text-sm text-slate-500">Providers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Highlights */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="p-4 flex items-center gap-3">
                  <Activity className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold text-blue-900">{analytics?.today_appointments || 0}</p>
                    <p className="text-xs text-blue-600">Today's Appointments</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardContent className="p-4 flex items-center gap-3">
                  <DollarSign className="w-8 h-8 text-emerald-500" />
                  <div>
                    <p className="text-2xl font-bold text-emerald-900">₹{analytics?.month_revenue || 0}</p>
                    <p className="text-xs text-emerald-600">This Month Revenue</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-violet-200 bg-violet-50/50">
                <CardContent className="p-4 flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 text-violet-500" />
                  <div>
                    <p className="text-2xl font-bold text-violet-900">{analytics?.new_users_week || 0}</p>
                    <p className="text-xs text-violet-600">New Users This Week</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Analytics Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Top Booked Services */}
              <Card data-testid="top-services-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#0F62FE]" />Top Booked Services</CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.top_services?.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.top_services.map((svc, i) => {
                        const maxCount = analytics.top_services[0]?.count || 1;
                        return (
                          <div key={i} data-testid={`service-stat-${i}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-700">{svc.name}</span>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-blue-100 text-blue-700 text-xs">{svc.count} bookings</Badge>
                                {svc.revenue > 0 && <span className="text-xs text-emerald-600 font-medium">₹{svc.revenue}</span>}
                              </div>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2">
                              <div className="bg-gradient-to-r from-[#0F62FE] to-blue-400 h-2 rounded-full transition-all" style={{width: `${(svc.count / maxCount) * 100}%`}} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm text-center py-4">No bookings yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Providers */}
              <Card data-testid="top-providers-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" />Top Providers</CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.top_providers?.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.top_providers.map((prov, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50" data-testid={`provider-stat-${i}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-700' : 'bg-orange-100 text-orange-700'}`}>
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">{prov.name}</p>
                            <p className="text-xs text-slate-500 capitalize">{prov.role}</p>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-700">{prov.bookings} bookings</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm text-center py-4">No providers yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Second Row */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Monthly Trends */}
              <Card data-testid="monthly-trends-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-500" />Monthly Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.monthly_trends?.length > 0 ? (
                    <div className="space-y-2">
                      {analytics.monthly_trends.map((m, i) => {
                        const maxM = Math.max(...analytics.monthly_trends.map(x => x.count)) || 1;
                        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                        const parts = m.month.split('-');
                        const label = parts.length === 2 ? `${monthNames[parseInt(parts[1])-1]} ${parts[0]}` : m.month;
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-sm text-slate-600 w-20">{label}</span>
                            <div className="flex-1 bg-slate-100 rounded-full h-3">
                              <div className="bg-gradient-to-r from-emerald-500 to-emerald-300 h-3 rounded-full" style={{width: `${(m.count / maxM) * 100}%`}} />
                            </div>
                            <span className="text-sm font-medium text-slate-700 w-8 text-right">{m.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm text-center py-4">No data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* User Distribution + Appointment Status */}
              <div className="space-y-6">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">User Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        {label: 'Patients', count: analytics?.users?.patients || 0, color: 'bg-blue-500'},
                        {label: 'Doctors', count: analytics?.users?.doctors || 0, color: 'bg-emerald-500'},
                        {label: 'Nurses', count: analytics?.users?.nurses || 0, color: 'bg-purple-500'},
                      ].map((item, i) => {
                        const total = analytics?.users?.total || 1;
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-sm text-slate-600 w-16">{item.label}</span>
                            <div className="flex-1 bg-slate-100 rounded-full h-2.5">
                              <div className={`${item.color} h-2.5 rounded-full`} style={{width: `${(item.count / total) * 100}%`}} />
                            </div>
                            <span className="text-sm font-semibold w-8 text-right">{item.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Appointment Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
                        <p className="text-2xl font-bold text-amber-800">{analytics?.appointments?.pending || 0}</p>
                        <p className="text-xs text-amber-600">Pending</p>
                      </div>
                      <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                        <p className="text-2xl font-bold text-blue-800">{analytics?.appointments?.confirmed || 0}</p>
                        <p className="text-xs text-blue-600">Confirmed</p>
                      </div>
                      <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                        <p className="text-2xl font-bold text-emerald-800">{analytics?.appointments?.completed || 0}</p>
                        <p className="text-xs text-emerald-600">Completed</p>
                      </div>
                      <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
                        <p className="text-2xl font-bold text-red-800">{analytics?.appointments?.cancelled || 0}</p>
                        <p className="text-xs text-red-600">Cancelled</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Pending Approvals Alert */}
            {pendingProviders.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <AlertTriangle className="w-8 h-8 text-amber-600" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-900">Pending Approvals</h3>
                      <p className="text-amber-700">{pendingProviders.length} provider(s) waiting for approval</p>
                    </div>
                    <Button onClick={() => setActiveTab('approvals')}>
                      Review Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals">
            <Card>
              <CardHeader>
                <CardTitle>Pending Provider Approvals</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingProviders.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">All caught up!</h3>
                    <p className="text-slate-600">No pending approvals at the moment</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingProviders.map((provider, index) => (
                      <div 
                        key={provider.id} 
                        className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl border border-slate-200"
                        data-testid={`pending-provider-${index}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-lg font-medium text-slate-600">
                            {getInitials(provider.user?.name || 'P')}
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{provider.user?.name || 'Provider'}</h4>
                            <p className="text-sm text-[#0F62FE] capitalize">{provider.user?.role} • {provider.specialization || 'N/A'}</p>
                            <p className="text-sm text-slate-500">Phone: {provider.user?.phone}</p>
                            <p className="text-sm text-slate-500">License: {provider.license_number || 'Not provided'}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="flex gap-2 text-sm text-slate-500 flex-wrap">
                            {provider.license_document && (
                              <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                                <FileText className="w-3 h-3 mr-1" />
                                License
                              </Badge>
                            )}
                            {provider.id_proof_document && (
                              <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                                <FileText className="w-3 h-3 mr-1" />
                                ID Proof
                              </Badge>
                            )}
                            {provider.certificates && provider.certificates.length > 0 && (
                              <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                                <FileText className="w-3 h-3 mr-1" />
                                Certificate
                              </Badge>
                            )}
                            {provider.user?.avatar && (
                              <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                                <FileText className="w-3 h-3 mr-1" />
                                Photo
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              className="text-[#0F62FE] border-[#0F62FE] hover:bg-blue-50"
                              onClick={() => setViewProfile(provider)}
                              data-testid={`view-profile-btn-${index}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View Profile
                            </Button>
                            <Button
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleApproveProvider(provider.user_id, true)}
                              data-testid={`approve-btn-${index}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleApproveProvider(provider.user_id, false)}
                              data-testid={`reject-btn-${index}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>All Providers</span>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search providers..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {allProviders
                    .filter(p => 
                      p.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      p.specialization?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((provider, index) => (
                      <div key={provider.id} className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-medium text-slate-600">
                            {getInitials(provider.user?.name || 'P')}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{provider.user?.name || 'Provider'}</p>
                            <p className="text-sm text-slate-500 capitalize">{provider.user?.role} • {provider.specialization || provider.lab_name || 'N/A'}</p>
                          </div>
                        </div>
                        <Badge className={getStatusColor(provider.approval_status === 'approved' ? 'completed' : provider.approval_status)}>
                          {provider.approval_status}
                        </Badge>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <CardTitle className="flex items-center justify-between">
                    <span>All Users</span>
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </CardTitle>
                  {/* Provider verification filter pills */}
                  {(() => {
                    const providers = users.filter(u => u.role === 'doctor' || u.role === 'nurse');
                    const counts = {
                      all: users.length,
                      incomplete: providers.filter(u => !u.onboarding_completed).length,
                      pending: providers.filter(u => u.onboarding_completed && u.approval_status === 'pending').length,
                      approved: providers.filter(u => u.approval_status === 'approved').length,
                      rejected: providers.filter(u => u.approval_status === 'rejected').length,
                    };
                    return (
                      <div className="flex gap-2 flex-wrap" data-testid="user-verification-filters">
                        {[
                          { key: 'all', label: 'All', count: counts.all, color: 'slate' },
                          { key: 'incomplete', label: 'Onboarding Incomplete', count: counts.incomplete, color: 'amber' },
                          { key: 'pending', label: 'Awaiting Review', count: counts.pending, color: 'blue' },
                          { key: 'approved', label: 'Verified', count: counts.approved, color: 'emerald' },
                          { key: 'rejected', label: 'Rejected', count: counts.rejected, color: 'red' },
                        ].map((f) => (
                          <button
                            key={f.key}
                            onClick={() => setUserFilter(f.key)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                              userFilter === f.key
                                ? 'bg-[#3B1C73] text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                            data-testid={`user-filter-${f.key}`}
                          >
                            {f.label} <span className="ml-1 text-xs opacity-70">{f.count}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users
                    .filter(u =>
                      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      u.phone?.includes(searchQuery)
                    )
                    .filter(u => {
                      if (userFilter === 'all') return true;
                      if (u.role !== 'doctor' && u.role !== 'nurse') return false;
                      if (userFilter === 'incomplete') return !u.onboarding_completed;
                      if (userFilter === 'pending') return u.onboarding_completed && u.approval_status === 'pending';
                      if (userFilter === 'approved') return u.approval_status === 'approved';
                      if (userFilter === 'rejected') return u.approval_status === 'rejected';
                      return true;
                    })
                    .map((u, index) => {
                      const isProvider = u.role === 'doctor' || u.role === 'nurse';
                      return (
                        <div key={u.id} className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50" data-testid={`user-row-${index}`}>
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-medium text-slate-600">
                              {getInitials(u.name || u.phone)}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{u.name || 'User'}</p>
                              <p className="text-sm text-slate-500">
                                {u.phone} • <span className="capitalize">{u.role}</span>
                                {isProvider && u.specialization && <> • <span className="capitalize">{u.specialization.replace(/_/g, ' ')}</span></>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap justify-end">
                            {isProvider && (
                              <>
                                {u.onboarding_completed ? (
                                  <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    <CheckCircle className="w-3 h-3 mr-1" /> Onboarded
                                  </Badge>
                                ) : (
                                  <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                                    <AlertTriangle className="w-3 h-3 mr-1" /> Onboarding Incomplete
                                  </Badge>
                                )}
                                {u.onboarding_completed && (
                                  u.approval_status === 'approved' ? (
                                    <Badge className="bg-emerald-100 text-emerald-800">Verified</Badge>
                                  ) : u.approval_status === 'rejected' ? (
                                    <Badge className="bg-red-100 text-red-800">Rejected</Badge>
                                  ) : (
                                    <Badge className="bg-blue-100 text-blue-800">Awaiting Review</Badge>
                                  )
                                )}
                                {/* Quick approve/reject for onboarded but pending */}
                                {u.onboarding_completed && u.approval_status !== 'approved' && (
                                  <button
                                    onClick={() => handleApproveProvider(u.id, true)}
                                    className="px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium"
                                    title="Approve"
                                    data-testid={`approve-user-${index}`}
                                  >
                                    Approve
                                  </button>
                                )}
                                {u.onboarding_completed && u.approval_status !== 'rejected' && (
                                  <button
                                    onClick={() => handleApproveProvider(u.id, false)}
                                    className="px-2.5 py-1 rounded-md bg-slate-100 hover:bg-red-50 text-slate-700 hover:text-red-700 text-xs font-medium"
                                    title="Reject"
                                    data-testid={`reject-user-${index}`}
                                  >
                                    Reject
                                  </button>
                                )}
                              </>
                            )}
                            {!isProvider && u.is_verified && (
                              <Badge className="bg-emerald-100 text-emerald-800">Verified</Badge>
                            )}
                            <Badge variant="outline" className="capitalize">{u.role}</Badge>
                            <button
                              onClick={() => handleViewUser(u.id)}
                              className="p-1.5 text-[#3B1C73] hover:bg-purple-50 rounded-lg transition-colors"
                              title="View Details"
                              data-testid={`view-user-${index}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {u.role !== 'admin' && (
                              <button
                                onClick={() => handleDeleteUser(u.id, u.name || u.phone)}
                                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete User"
                                data-testid={`delete-user-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <CardTitle>Recent Appointments</CardTitle>
              </CardHeader>
              <CardContent>
                {appointments.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">No appointments yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {appointments.slice(0, 20).map((apt) => (
                      <div key={apt.id} className="p-4 rounded-lg border border-slate-100 hover:bg-slate-50 space-y-2" data-testid={`admin-appt-${apt.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-mono text-slate-500" data-testid="admin-appt-booking-id">#{apt.id.slice(0, 8).toUpperCase()}</span>
                              <Badge className={getStatusColor(apt.status)}>{apt.status}</Badge>
                              <Badge variant="outline" className="capitalize text-xs">{apt.service_type?.replace('_', ' ')}</Badge>
                            </div>
                            <div className="mt-2 grid sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">
                              <div>
                                <span className="text-slate-500">Patient: </span>
                                <span className="font-medium text-slate-900" data-testid="admin-appt-patient">{apt.patient_name || '—'}</span>
                                {apt.patient_phone && <span className="text-xs text-slate-500 ml-1">({apt.patient_phone})</span>}
                              </div>
                              <div>
                                <span className="text-slate-500">Provider: </span>
                                <span className="font-medium text-slate-900" data-testid="admin-appt-provider">{apt.provider_name || '—'}</span>
                                {apt.provider_specialization && <span className="text-xs text-slate-500 ml-1 capitalize">({apt.provider_specialization.replace(/_/g, ' ')})</span>}
                              </div>
                              <div>
                                <span className="text-slate-500">Date & Time: </span>
                                <span className="font-medium text-slate-900">{formatDate(apt.appointment_date)} · {apt.time_slot}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Location: </span>
                                <span className="font-medium text-slate-900" data-testid="admin-appt-location">{apt.location_display || '—'}</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-bold text-slate-900" data-testid="admin-appt-fee">₹{apt.fee}</span>
                            {apt.payment_status && <p className="text-xs text-slate-500 capitalize">{apt.payment_status}</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inbox Tab */}
          <TabsContent value="inbox">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Inbox className="w-5 h-5 text-[#3B1C73]" />
                      Contact Inbox
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">Messages submitted via the Contact Us form on physign.com</p>
                  </div>
                  <div className="flex gap-2 flex-wrap" data-testid="inbox-filters">
                    {[
                      { key: 'all', label: 'All', count: messageCounts.total },
                      { key: 'new', label: 'New', count: messageCounts.new },
                      { key: 'replied', label: 'Replied', count: messageCounts.replied },
                      { key: 'closed', label: 'Closed', count: messageCounts.closed },
                    ].map((f) => (
                      <button
                        key={f.key}
                        onClick={() => setMessageFilter(f.key)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          messageFilter === f.key
                            ? 'bg-[#3B1C73] text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`inbox-filter-${f.key}`}
                      >
                        {f.label} <span className="ml-1 opacity-70">({f.count})</span>
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {messagesWarning && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <strong>Setup required:</strong> {messagesWarning}
                    <div className="mt-2 text-xs text-amber-700">
                      Supabase Dashboard → SQL Editor mein <code>/app/backend/supabase_schema.sql</code> ka <code>contact_messages</code> table block run kar dijiye.
                    </div>
                  </div>
                )}
                {(() => {
                  const filtered = messageFilter === 'all'
                    ? messages
                    : messages.filter((m) => (m.status || 'new') === messageFilter);
                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-12 text-slate-500" data-testid="inbox-empty">
                        <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p>No messages {messageFilter !== 'all' ? `in "${messageFilter}"` : ''} yet.</p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2" data-testid="inbox-list">
                      {filtered.map((m) => {
                        const isOpen = expandedMessageId === m.id;
                        const statusColor = m.status === 'replied'
                          ? 'bg-green-100 text-green-800'
                          : m.status === 'closed'
                          ? 'bg-slate-100 text-slate-700'
                          : 'bg-purple-100 text-purple-800';
                        return (
                          <div
                            key={m.id}
                            className={`border rounded-xl transition-colors ${isOpen ? 'border-[#3B1C73]/40 bg-purple-50/30' : 'border-slate-200 hover:border-slate-300'}`}
                            data-testid={`message-row-${m.id}`}
                          >
                            <button
                              onClick={() => setExpandedMessageId(isOpen ? null : m.id)}
                              className="w-full p-4 flex items-start gap-4 text-left"
                            >
                              <div className="w-10 h-10 rounded-full bg-[#3B1C73]/10 flex items-center justify-center text-sm font-bold text-[#3B1C73] flex-shrink-0">
                                {getInitials(m.name || 'U')}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-slate-900">{m.name}</span>
                                  <Badge className={`${statusColor} capitalize`}>{m.status || 'new'}</Badge>
                                  <span className="text-xs text-slate-500 ml-auto">{formatDate(m.created_at)}</span>
                                </div>
                                <p className="text-sm font-medium text-slate-700 mt-1 truncate">
                                  {m.subject || '(No subject)'}
                                </p>
                                <p className="text-sm text-slate-500 mt-1 truncate">{m.message}</p>
                              </div>
                            </button>

                            {isOpen && (
                              <div className="px-4 pb-4 pl-[72px] space-y-3 border-t border-slate-100 pt-3">
                                <div className="grid sm:grid-cols-3 gap-3 text-sm">
                                  {m.email && (
                                    <div>
                                      <p className="text-xs text-slate-500">Email</p>
                                      <a href={`mailto:${m.email}`} className="text-[#3B1C73] hover:underline break-all">{m.email}</a>
                                    </div>
                                  )}
                                  {m.phone && (
                                    <div>
                                      <p className="text-xs text-slate-500">Phone</p>
                                      <a href={`tel:${m.phone}`} className="text-[#3B1C73] hover:underline">{m.phone}</a>
                                    </div>
                                  )}
                                  {m.city && (
                                    <div>
                                      <p className="text-xs text-slate-500">City</p>
                                      <p className="text-slate-900">{m.city}</p>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500 mb-1">Message</p>
                                  <p className="text-sm text-slate-800 whitespace-pre-wrap bg-white border border-slate-200 rounded-lg p-3">
                                    {m.message}
                                  </p>
                                </div>
                                <div className="flex gap-2 flex-wrap pt-2">
                                  {m.email && (
                                    <a
                                      href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || 'Your message to Physign')}`}
                                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#3B1C73] text-white text-sm font-medium hover:bg-[#2c1457]"
                                      data-testid={`reply-email-${m.id}`}
                                    >
                                      <MailOpen className="w-4 h-4" /> Reply via Email
                                    </a>
                                  )}
                                  {m.phone && (
                                    <a
                                      href={`https://wa.me/${(m.phone || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi ${m.name}, thanks for reaching out to Physign. `)}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700"
                                      data-testid={`reply-wa-${m.id}`}
                                    >
                                      <PhoneIcon className="w-4 h-4" /> WhatsApp
                                    </a>
                                  )}
                                  {m.status !== 'replied' && (
                                    <button
                                      onClick={() => handleMessageStatus(m.id, 'replied')}
                                      className="px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-sm font-medium hover:bg-green-100"
                                      data-testid={`mark-replied-${m.id}`}
                                    >
                                      Mark Replied
                                    </button>
                                  )}
                                  {m.status !== 'closed' && (
                                    <button
                                      onClick={() => handleMessageStatus(m.id, 'closed')}
                                      className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200"
                                      data-testid={`mark-closed-${m.id}`}
                                    >
                                      Mark Closed
                                    </button>
                                  )}
                                  {m.status !== 'new' && (
                                    <button
                                      onClick={() => handleMessageStatus(m.id, 'new')}
                                      className="px-3 py-1.5 rounded-lg bg-purple-50 text-purple-700 text-sm font-medium hover:bg-purple-100"
                                      data-testid={`mark-new-${m.id}`}
                                    >
                                      Mark as New
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteMessage(m.id)}
                                    className="ml-auto px-3 py-1.5 rounded-lg text-red-600 hover:bg-red-50 text-sm font-medium inline-flex items-center gap-1"
                                    data-testid={`delete-message-${m.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" /> Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="blogs">
            <BlogManager isAdmin />
          </TabsContent>
        </Tabs>
      </main>

      {/* Provider Profile Detail Modal */}
      {viewProfile && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="profile-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b p-5 rounded-t-2xl flex items-center justify-between z-10">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#0F62FE]/10 flex items-center justify-center text-lg font-bold text-[#0F62FE]">
                  {getInitials(viewProfile.user?.name || 'P')}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{viewProfile.user?.name || 'Provider'}</h2>
                  <Badge className="bg-blue-100 text-blue-800 capitalize">{viewProfile.user?.role}</Badge>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setViewProfile(null)} data-testid="close-profile-modal">
                <XCircle className="w-5 h-5" />
              </Button>
            </div>

            {/* Profile Content */}
            <div className="p-5 space-y-5">
              {/* Personal Details */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Personal Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="font-medium text-slate-900">{viewProfile.user?.phone || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium text-slate-900">{viewProfile.user?.email || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Location</p>
                    <p className="font-medium text-slate-900">{viewProfile.location || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Clinic / Hospital</p>
                    <p className="font-medium text-slate-900">{viewProfile.clinic_name || 'N/A'}</p>
                  </div>
                  {viewProfile.clinic_address && (
                    <div className="p-3 bg-slate-50 rounded-lg col-span-2">
                      <p className="text-xs text-slate-500">Clinic Address</p>
                      <p className="font-medium text-slate-900">{viewProfile.clinic_address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Professional Details */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Professional Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Specialization</p>
                    <p className="font-medium text-slate-900 capitalize">{viewProfile.specialization?.replace(/_/g, ' ') || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Qualification</p>
                    <p className="font-medium text-slate-900 uppercase">{viewProfile.qualification || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Experience</p>
                    <p className="font-medium text-slate-900">{viewProfile.experience_years ? `${viewProfile.experience_years} years` : 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Registration Number</p>
                    <p className="font-medium text-slate-900">{viewProfile.medical_registration_number || viewProfile.license_number || 'Not provided'}</p>
                  </div>
                </div>
                {viewProfile.bio && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Bio</p>
                    <p className="text-sm text-slate-700 mt-1">{(() => {
                      try { return JSON.parse(viewProfile.bio)?.bio_text || viewProfile.bio; } catch { return viewProfile.bio; }
                    })()}</p>
                  </div>
                )}
              </div>

              {/* Fees & Services */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Fees & Services</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Consultation Fee</p>
                    <p className="font-medium text-slate-900">₹{viewProfile.consultation_fee || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Home Visit Fee</p>
                    <p className="font-medium text-slate-900">₹{viewProfile.home_visit_fee || 'N/A'}</p>
                  </div>
                </div>
                {viewProfile.services?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {viewProfile.services.map(s => (
                      <Badge key={s} variant="outline" className="capitalize">{s.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Availability */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Availability</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Available Days</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(viewProfile.available_days || []).map(d => (
                        <Badge key={d} className="bg-blue-100 text-blue-800 text-xs">{d}</Badge>
                      ))}
                      {(!viewProfile.available_days || viewProfile.available_days.length === 0) && <p className="text-sm text-slate-500">Not set</p>}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Time Slots</p>
                    {viewProfile.time_slots?.length > 0 ? viewProfile.time_slots.map((ts, i) => (
                      <p key={i} className="font-medium text-slate-900">{ts.start} - {ts.end}</p>
                    )) : <p className="text-sm text-slate-500">Not set</p>}
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Documents</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {/* License (only for nurse — doctors don't upload license anymore) */}
                  {viewProfile.user?.role !== 'doctor' && (
                    <div className={`p-3 rounded-lg border-2 text-center ${viewProfile.license_document ? 'border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md transition-shadow' : 'border-slate-200 bg-slate-50'}`}
                      onClick={() => viewProfile.license_document && window.open(getDocUrl(viewProfile.license_document), '_blank')}
                      data-testid="admin-view-license"
                    >
                      {viewProfile.license_document && viewProfile.license_document.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                        <img src={getDocUrl(viewProfile.license_document)} alt="License" className="w-12 h-12 object-cover rounded mx-auto mb-1 border" />
                      ) : (
                        <FileText className={`w-6 h-6 mx-auto mb-1 ${viewProfile.license_document ? 'text-emerald-600' : 'text-slate-400'}`} />
                      )}
                      <p className="text-xs font-medium">Nursing Certificate</p>
                      <p className={`text-xs mt-1 ${viewProfile.license_document ? 'text-emerald-600 font-medium' : 'text-red-500'}`}>
                        {viewProfile.license_document ? 'Click to View' : 'Not uploaded'}
                      </p>
                    </div>
                  )}
                  <div className={`p-3 rounded-lg border-2 text-center ${viewProfile.id_proof_document ? 'border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md transition-shadow' : 'border-slate-200 bg-slate-50'}`}
                    onClick={() => viewProfile.id_proof_document && window.open(getDocUrl(viewProfile.id_proof_document), '_blank')}
                    data-testid="admin-view-idproof"
                  >
                    {viewProfile.id_proof_document && viewProfile.id_proof_document.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                      <img src={getDocUrl(viewProfile.id_proof_document)} alt="ID Proof" className="w-12 h-12 object-cover rounded mx-auto mb-1 border" />
                    ) : (
                      <FileText className={`w-6 h-6 mx-auto mb-1 ${viewProfile.id_proof_document ? 'text-emerald-600' : 'text-slate-400'}`} />
                    )}
                    <p className="text-xs font-medium">ID Proof / Aadhar</p>
                    <p className={`text-xs mt-1 ${viewProfile.id_proof_document ? 'text-emerald-600 font-medium' : 'text-red-500'}`}>
                      {viewProfile.id_proof_document ? 'Click to View' : 'Not uploaded'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg border-2 text-center ${viewProfile.certificates?.[0] ? 'border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md transition-shadow' : 'border-slate-200 bg-slate-50'}`}
                    onClick={() => viewProfile.certificates?.[0] && window.open(getDocUrl(viewProfile.certificates[0]), '_blank')}
                    data-testid="admin-view-certificates"
                  >
                    {viewProfile.certificates?.[0] && viewProfile.certificates[0].match(/\.(jpg|jpeg|png|webp)$/i) ? (
                      <img src={getDocUrl(viewProfile.certificates[0])} alt="Certificate" className="w-12 h-12 object-cover rounded mx-auto mb-1 border" />
                    ) : (
                      <FileText className={`w-6 h-6 mx-auto mb-1 ${viewProfile.certificates?.[0] ? 'text-emerald-600' : 'text-slate-400'}`} />
                    )}
                    <p className="text-xs font-medium">Degree Certificate</p>
                    <p className={`text-xs mt-1 ${viewProfile.certificates?.[0] ? 'text-emerald-600 font-medium' : 'text-red-500'}`}>
                      {viewProfile.certificates?.[0] ? 'Click to View' : 'Not uploaded'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg border-2 text-center ${viewProfile.certificates?.[1] ? 'border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md transition-shadow' : 'border-slate-200 bg-slate-50'}`}
                    onClick={() => viewProfile.certificates?.[1] && window.open(getDocUrl(viewProfile.certificates[1]), '_blank')}
                    data-testid="admin-view-registration-cert"
                  >
                    {viewProfile.certificates?.[1] && viewProfile.certificates[1].match(/\.(jpg|jpeg|png|webp)$/i) ? (
                      <img src={getDocUrl(viewProfile.certificates[1])} alt="Registration Certificate" className="w-12 h-12 object-cover rounded mx-auto mb-1 border" />
                    ) : (
                      <FileText className={`w-6 h-6 mx-auto mb-1 ${viewProfile.certificates?.[1] ? 'text-emerald-600' : 'text-slate-400'}`} />
                    )}
                    <p className="text-xs font-medium">Registration Certificate</p>
                    <p className={`text-xs mt-1 ${viewProfile.certificates?.[1] ? 'text-emerald-600 font-medium' : 'text-red-500'}`}>
                      {viewProfile.certificates?.[1] ? 'Click to View' : 'Not uploaded'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg border-2 text-center ${viewProfile.user?.avatar ? 'border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md transition-shadow' : 'border-slate-200 bg-slate-50'}`}
                    onClick={() => viewProfile.user?.avatar && window.open(getDocUrl(viewProfile.user.avatar), '_blank')}
                    data-testid="admin-view-photo"
                  >
                    {viewProfile.user?.avatar ? (
                      <img src={getDocUrl(viewProfile.user.avatar)} alt="Photo" className="w-12 h-12 object-cover rounded-full mx-auto mb-1 border" />
                    ) : (
                      <FileText className="w-6 h-6 mx-auto mb-1 text-slate-400" />
                    )}
                    <p className="text-xs font-medium">Passport Photo</p>
                    <p className={`text-xs mt-1 ${viewProfile.user?.avatar ? 'text-emerald-600 font-medium' : 'text-red-500'}`}>
                      {viewProfile.user?.avatar ? 'Click to View' : 'Not uploaded'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Nurse-specific data */}
              {viewProfile.user?.role === 'nurse' && (() => {
                let nurseData = {};
                try { nurseData = JSON.parse(viewProfile.bio)?.nurse_data || {}; } catch {}
                return Object.keys(nurseData).length > 0 ? (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Additional Nurse Details</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {nurseData.father_husband_name && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Father/Husband Name</p>
                          <p className="font-medium text-slate-900">{nurseData.father_husband_name}</p>
                        </div>
                      )}
                      {nurseData.state_nursing_council && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Nursing Council</p>
                          <p className="font-medium text-slate-900">{nurseData.state_nursing_council}</p>
                        </div>
                      )}
                      {nurseData.current_hospital && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Current Hospital</p>
                          <p className="font-medium text-slate-900">{nurseData.current_hospital}</p>
                        </div>
                      )}
                      {nurseData.preferred_area && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Preferred Area</p>
                          <p className="font-medium text-slate-900">{nurseData.preferred_area}</p>
                        </div>
                      )}
                      {nurseData.bank_name && (
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Bank</p>
                          <p className="font-medium text-slate-900">{nurseData.bank_name}</p>
                        </div>
                      )}
                      {nurseData.declaration_accepted && (
                        <div className="p-3 bg-emerald-50 rounded-lg">
                          <p className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Declaration Accepted</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Modal Footer - Approve/Reject Actions */}
            <div className="sticky bottom-0 bg-white border-t p-4 rounded-b-2xl flex justify-end gap-3">
              <Button variant="outline" onClick={() => setViewProfile(null)}>Close</Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => { handleApproveProvider(viewProfile.user_id, true); setViewProfile(null); }}
                data-testid="modal-approve-btn"
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => { handleApproveProvider(viewProfile.user_id, false); setViewProfile(null); }}
                data-testid="modal-reject-btn"
              >
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal — shows full info + documents for any user role */}
      {viewUserDetails && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewUserDetails(null)} data-testid="user-details-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 p-6 flex items-start justify-between">
              <div className="flex items-center gap-4">
                {viewUserDetails.user?.avatar ? (
                  <img src={getDocUrl(viewUserDetails.user.avatar)} alt={viewUserDetails.user.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#3B1C73]/20" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#3B1C73]/10 flex items-center justify-center text-xl font-bold text-[#3B1C73]">
                    {getInitials(viewUserDetails.user?.name || viewUserDetails.user?.phone || 'U')}
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{viewUserDetails.user?.name || 'No name'}</h2>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge className="bg-[#3B1C73]/10 text-[#3B1C73] capitalize">{viewUserDetails.user?.role}</Badge>
                    {viewUserDetails.user?.is_verified && <Badge className="bg-emerald-100 text-emerald-800">Verified</Badge>}
                    {viewUserDetails.user?.is_active === false && <Badge className="bg-red-100 text-red-800">Inactive</Badge>}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setViewUserDetails(null)} data-testid="close-user-details">
                <XCircle className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-5">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Account Info</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="font-medium text-slate-900">{viewUserDetails.user?.phone || '—'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium text-slate-900 break-all">{viewUserDetails.user?.email || '—'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Joined</p>
                    <p className="font-medium text-slate-900">{viewUserDetails.user?.created_at ? formatDate(viewUserDetails.user.created_at) : '—'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Total Appointments</p>
                    <p className="font-medium text-slate-900">{viewUserDetails.appointments_count || 0}</p>
                  </div>
                </div>
              </div>

              {/* Patient-specific */}
              {viewUserDetails.user?.role === 'patient' && viewUserDetails.profile && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Patient Profile</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {viewUserDetails.profile.date_of_birth && (
                      <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Date of Birth</p><p className="font-medium">{viewUserDetails.profile.date_of_birth}</p></div>
                    )}
                    {viewUserDetails.profile.gender && (
                      <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Gender</p><p className="font-medium capitalize">{viewUserDetails.profile.gender}</p></div>
                    )}
                    {viewUserDetails.profile.blood_group && (
                      <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Blood Group</p><p className="font-medium">{viewUserDetails.profile.blood_group}</p></div>
                    )}
                    {viewUserDetails.profile.address && (
                      <div className="p-3 bg-slate-50 rounded-lg col-span-2"><p className="text-xs text-slate-500">Address</p><p className="font-medium">{viewUserDetails.profile.address}</p></div>
                    )}
                    {viewUserDetails.profile.emergency_contact && (
                      <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Emergency Contact</p><p className="font-medium">{viewUserDetails.profile.emergency_contact}</p></div>
                    )}
                  </div>
                </div>
              )}

              {/* Provider-specific quick info + link to full profile */}
              {(viewUserDetails.user?.role === 'doctor' || viewUserDetails.user?.role === 'nurse') && viewUserDetails.profile && (
                <>
                  {/* Onboarding status banner */}
                  <div className={`p-4 rounded-xl flex items-start gap-3 ${viewUserDetails.profile.onboarding_completed ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                    {viewUserDetails.profile.onboarding_completed ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${viewUserDetails.profile.onboarding_completed ? 'text-emerald-900' : 'text-amber-900'}`}>
                        {viewUserDetails.profile.onboarding_completed ? 'Onboarding Submitted — Ready for Review' : 'Onboarding Incomplete'}
                      </p>
                      <p className={`text-xs mt-0.5 ${viewUserDetails.profile.onboarding_completed ? 'text-emerald-700' : 'text-amber-700'}`}>
                        Approval Status: <span className="font-medium capitalize">{viewUserDetails.profile.approval_status || 'pending'}</span>
                      </p>
                    </div>
                  </div>

                  {/* Step 1: Personal Information */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#3B1C73] text-white text-xs flex items-center justify-center font-bold">1</span>
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {viewUserDetails.profile.gender && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Gender</p><p className="font-medium capitalize">{viewUserDetails.profile.gender}</p></div>}
                      {viewUserDetails.profile.date_of_birth && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Date of Birth</p><p className="font-medium">{viewUserDetails.profile.date_of_birth}</p></div>}
                      {viewUserDetails.profile.languages?.length > 0 && <div className="p-3 bg-slate-50 rounded-lg col-span-2"><p className="text-xs text-slate-500">Languages</p><p className="font-medium">{viewUserDetails.profile.languages.join(', ')}</p></div>}
                      {viewUserDetails.profile.address_line1 && <div className="p-3 bg-slate-50 rounded-lg col-span-2"><p className="text-xs text-slate-500">Address</p><p className="font-medium">{viewUserDetails.profile.address_line1}</p></div>}
                      {viewUserDetails.profile.location && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">City</p><p className="font-medium">{viewUserDetails.profile.location}</p></div>}
                      {viewUserDetails.profile.state && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">State</p><p className="font-medium">{viewUserDetails.profile.state}</p></div>}
                      {viewUserDetails.profile.pin_code && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">PIN Code</p><p className="font-medium">{viewUserDetails.profile.pin_code}</p></div>}
                      {viewUserDetails.profile.country && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Country</p><p className="font-medium">{viewUserDetails.profile.country}</p></div>}
                    </div>
                    {viewUserDetails.profile.bio && !viewUserDetails.profile.bio.startsWith('{') && (
                      <div className="p-3 bg-slate-50 rounded-lg mt-3"><p className="text-xs text-slate-500">Bio</p><p className="font-medium text-sm whitespace-pre-wrap">{viewUserDetails.profile.bio}</p></div>
                    )}
                  </div>

                  {/* Step 2: Professional Info */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#3B1C73] text-white text-xs flex items-center justify-center font-bold">2</span>
                      Professional Details
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {viewUserDetails.profile.specialization && (
                        <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Specialization</p><p className="font-medium capitalize">{viewUserDetails.profile.specialization.replace(/_/g, ' ')}</p></div>
                      )}
                      {viewUserDetails.profile.qualification && (
                        <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Qualification</p><p className="font-medium">{viewUserDetails.profile.qualification}</p></div>
                      )}
                      {viewUserDetails.profile.experience_years != null && (
                        <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Experience</p><p className="font-medium">{viewUserDetails.profile.experience_years}+ years</p></div>
                      )}
                      {(viewUserDetails.profile.medical_registration_number || viewUserDetails.profile.license_number) && (
                        <div className="p-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500">Registration Number</p><p className="font-medium">{viewUserDetails.profile.medical_registration_number || viewUserDetails.profile.license_number}</p></div>
                      )}
                      {viewUserDetails.profile.services?.length > 0 && (
                        <div className="p-3 bg-slate-50 rounded-lg col-span-2"><p className="text-xs text-slate-500">Services Offered</p><p className="font-medium capitalize">{viewUserDetails.profile.services.map(s => s.replace(/_/g, ' ')).join(', ')}</p></div>
                      )}
                    </div>
                  </div>

                  {/* Step 3: Education */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#3B1C73] text-white text-xs flex items-center justify-center font-bold">3</span>
                      Education
                      {viewUserDetails.profile.education_history?.length > 0 && <Badge variant="outline" className="ml-auto">{viewUserDetails.profile.education_history.length}</Badge>}
                    </h3>
                    {viewUserDetails.profile.education_history?.length > 0 ? (
                      <div className="space-y-2">
                        {viewUserDetails.profile.education_history.map((edu, i) => (
                          <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid={`admin-edu-${i}`}>
                            <p className="font-semibold text-sm text-slate-900">{edu.degree}{edu.field && ` — ${edu.field}`}</p>
                            <p className="text-xs text-slate-600 mt-0.5">{edu.college}{edu.university && ` · ${edu.university}`}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{edu.start_year || '?'} – {edu.end_year || 'Present'}{edu.city && ` · ${edu.city}`}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic p-3 bg-slate-50 rounded-lg">No education added</p>
                    )}
                  </div>

                  {/* Step 4: Practice Locations */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#3B1C73] text-white text-xs flex items-center justify-center font-bold">4</span>
                      Practice Locations
                      {viewUserDetails.profile.practice_locations?.length > 0 && <Badge variant="outline" className="ml-auto">{viewUserDetails.profile.practice_locations.length}</Badge>}
                    </h3>
                    {viewUserDetails.profile.practice_locations?.length > 0 ? (
                      <div className="space-y-2">
                        {viewUserDetails.profile.practice_locations.map((loc, i) => (
                          <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid={`admin-practice-${i}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-semibold text-sm text-slate-900">{loc.clinic_name || loc.name}</p>
                                <p className="text-xs text-slate-600 mt-0.5">{loc.address}{loc.city && `, ${loc.city}`}{loc.state && `, ${loc.state}`}{loc.pin_code && ` – ${loc.pin_code}`}</p>
                                {loc.phone && <p className="text-xs text-slate-500 mt-0.5">📞 {loc.phone}</p>}
                              </div>
                              {loc.is_current && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Current</Badge>}
                            </div>
                            <div className="flex gap-3 mt-2 text-xs text-slate-600">
                              {loc.consultation_fee && <span>Consultation: <strong>₹{loc.consultation_fee}</strong></span>}
                              {loc.home_visit_fee && <span>Home Visit: <strong>₹{loc.home_visit_fee}</strong></span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : viewUserDetails.profile.clinic_name ? (
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <p className="font-semibold text-sm text-slate-900">{viewUserDetails.profile.clinic_name}</p>
                        <p className="text-xs text-slate-600 mt-0.5">{viewUserDetails.profile.clinic_address}</p>
                        <div className="flex gap-3 mt-2 text-xs text-slate-600">
                          {viewUserDetails.profile.consultation_fee && <span>Consultation: <strong>₹{viewUserDetails.profile.consultation_fee}</strong></span>}
                          {viewUserDetails.profile.home_visit_fee && <span>Home Visit: <strong>₹{viewUserDetails.profile.home_visit_fee}</strong></span>}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic p-3 bg-slate-50 rounded-lg">No practice locations added</p>
                    )}
                  </div>

                  {/* Step 5: Availability */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#3B1C73] text-white text-xs flex items-center justify-center font-bold">5</span>
                      Availability
                    </h3>
                    {viewUserDetails.profile.availability && Object.keys(viewUserDetails.profile.availability).length > 0 ? (
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-2">
                        {viewUserDetails.profile.availability.weekly?.days ? (
                          <div>
                            <p className="text-xs font-semibold text-slate-500 mb-1.5">Weekly Schedule</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                              {Object.entries(viewUserDetails.profile.availability.weekly.days).map(([day, cfg]) => (
                                <div key={day} className={`p-2 rounded text-xs ${cfg?.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-500'}`}>
                                  <p className="font-semibold capitalize">{day.slice(0, 3)}</p>
                                  {cfg?.enabled && cfg?.slots?.length > 0 ? (
                                    <p>{cfg.slots.map(s => `${s.start}-${s.end}`).join(', ')}</p>
                                  ) : (
                                    <p>Off</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <pre className="text-xs text-slate-600 overflow-auto max-h-40 whitespace-pre-wrap">{JSON.stringify(viewUserDetails.profile.availability, null, 2)}</pre>
                        )}
                        {viewUserDetails.profile.availability.leaves?.length > 0 && (
                          <p className="text-xs text-slate-600"><strong>{viewUserDetails.profile.availability.leaves.length}</strong> leave(s) scheduled</p>
                        )}
                        {viewUserDetails.profile.availability.holidays?.length > 0 && (
                          <p className="text-xs text-slate-600"><strong>{viewUserDetails.profile.availability.holidays.length}</strong> holiday(s)</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic p-3 bg-slate-50 rounded-lg">No availability set</p>
                    )}
                  </div>

                  {/* Step 6: Experience */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#3B1C73] text-white text-xs flex items-center justify-center font-bold">6</span>
                      Work Experience
                      {viewUserDetails.profile.experience_history?.length > 0 && <Badge variant="outline" className="ml-auto">{viewUserDetails.profile.experience_history.length}</Badge>}
                    </h3>
                    {viewUserDetails.profile.experience_history?.length > 0 ? (
                      <div className="space-y-2">
                        {viewUserDetails.profile.experience_history.map((exp, i) => (
                          <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid={`admin-exp-${i}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <p className="font-semibold text-sm text-slate-900">{exp.designation}</p>
                                <p className="text-xs text-slate-600 mt-0.5">{exp.hospital}{exp.city && ` · ${exp.city}`}</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  {exp.start_date}{exp.end_date && ` → ${exp.end_date}`}{exp.is_current && ' → Present'}
                                  {exp.employment_type && ` · ${exp.employment_type.replace(/_/g, ' ')}`}
                                </p>
                                {exp.description && <p className="text-xs text-slate-600 mt-1 italic">&quot;{exp.description}&quot;</p>}
                              </div>
                              {exp.is_current && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Current</Badge>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic p-3 bg-slate-50 rounded-lg">No experience added</p>
                    )}
                  </div>

                  {/* Step 7a: Documents (existing) */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#3B1C73] text-white text-xs flex items-center justify-center font-bold">7</span>
                      Verification Documents
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {viewUserDetails.user?.role === 'nurse' && (
                        <div className={`p-3 rounded-lg border-2 text-center ${viewUserDetails.profile.license_document ? 'border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md' : 'border-slate-200 bg-slate-50'}`} onClick={() => viewUserDetails.profile.license_document && window.open(getDocUrl(viewUserDetails.profile.license_document), '_blank')}>
                          <FileText className={`w-6 h-6 mx-auto mb-1 ${viewUserDetails.profile.license_document ? 'text-emerald-600' : 'text-slate-400'}`} />
                          <p className="text-xs font-medium">Nursing Certificate</p>
                          <p className={`text-xs mt-1 ${viewUserDetails.profile.license_document ? 'text-emerald-600' : 'text-red-500'}`}>{viewUserDetails.profile.license_document ? 'View' : 'Not uploaded'}</p>
                        </div>
                      )}
                      <div className={`p-3 rounded-lg border-2 text-center ${viewUserDetails.profile.id_proof_document ? 'border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md' : 'border-slate-200 bg-slate-50'}`} onClick={() => viewUserDetails.profile.id_proof_document && window.open(getDocUrl(viewUserDetails.profile.id_proof_document), '_blank')}>
                        <FileText className={`w-6 h-6 mx-auto mb-1 ${viewUserDetails.profile.id_proof_document ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <p className="text-xs font-medium">ID Proof</p>
                        <p className={`text-xs mt-1 ${viewUserDetails.profile.id_proof_document ? 'text-emerald-600' : 'text-red-500'}`}>{viewUserDetails.profile.id_proof_document ? 'View' : 'Not uploaded'}</p>
                      </div>
                      <div className={`p-3 rounded-lg border-2 text-center ${viewUserDetails.profile.certificates?.[0] ? 'border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md' : 'border-slate-200 bg-slate-50'}`} onClick={() => viewUserDetails.profile.certificates?.[0] && window.open(getDocUrl(viewUserDetails.profile.certificates[0]), '_blank')}>
                        <FileText className={`w-6 h-6 mx-auto mb-1 ${viewUserDetails.profile.certificates?.[0] ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <p className="text-xs font-medium">Degree Certificate</p>
                        <p className={`text-xs mt-1 ${viewUserDetails.profile.certificates?.[0] ? 'text-emerald-600' : 'text-red-500'}`}>{viewUserDetails.profile.certificates?.[0] ? 'View' : 'Not uploaded'}</p>
                      </div>
                      <div className={`p-3 rounded-lg border-2 text-center ${viewUserDetails.profile.certificates?.[1] ? 'border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md' : 'border-slate-200 bg-slate-50'}`} onClick={() => viewUserDetails.profile.certificates?.[1] && window.open(getDocUrl(viewUserDetails.profile.certificates[1]), '_blank')}>
                        <FileText className={`w-6 h-6 mx-auto mb-1 ${viewUserDetails.profile.certificates?.[1] ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <p className="text-xs font-medium">Registration Certificate</p>
                        <p className={`text-xs mt-1 ${viewUserDetails.profile.certificates?.[1] ? 'text-emerald-600' : 'text-red-500'}`}>{viewUserDetails.profile.certificates?.[1] ? 'View' : 'Not uploaded'}</p>
                      </div>
                      <div className={`p-3 rounded-lg border-2 text-center ${viewUserDetails.user?.avatar ? 'border-emerald-200 bg-emerald-50 cursor-pointer hover:shadow-md' : 'border-slate-200 bg-slate-50'}`} onClick={() => viewUserDetails.user?.avatar && window.open(getDocUrl(viewUserDetails.user.avatar), '_blank')}>
                        {viewUserDetails.user?.avatar ? <img src={getDocUrl(viewUserDetails.user.avatar)} alt="" className="w-12 h-12 object-cover rounded-full mx-auto mb-1 border" /> : <FileText className="w-6 h-6 mx-auto mb-1 text-slate-400" />}
                        <p className="text-xs font-medium">Passport Photo</p>
                        <p className={`text-xs mt-1 ${viewUserDetails.user?.avatar ? 'text-emerald-600' : 'text-red-500'}`}>{viewUserDetails.user?.avatar ? 'View' : 'Not uploaded'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Step 7b: Bank Details */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#3B1C73] text-white text-xs flex items-center justify-center font-bold">7b</span>
                      Bank Details for Payouts
                    </h3>
                    {viewUserDetails.profile.bank_details && Object.keys(viewUserDetails.profile.bank_details).length > 0 ? (
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        {viewUserDetails.profile.bank_details.clinic_managed_payout && (
                          <Badge className="bg-amber-100 text-amber-800 mb-2">Payouts managed by clinic/hospital</Badge>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          {viewUserDetails.profile.bank_details.account_holder && <div><p className="text-xs text-slate-500">Account Holder</p><p className="font-medium text-sm">{viewUserDetails.profile.bank_details.account_holder}</p></div>}
                          {viewUserDetails.profile.bank_details.payout_method && <div><p className="text-xs text-slate-500">Method</p><p className="font-medium text-sm capitalize">{viewUserDetails.profile.bank_details.payout_method.replace(/_/g, ' ')}</p></div>}
                          {viewUserDetails.profile.bank_details.bank_name && <div><p className="text-xs text-slate-500">Bank</p><p className="font-medium text-sm">{viewUserDetails.profile.bank_details.bank_name}</p></div>}
                          {viewUserDetails.profile.bank_details.ifsc && <div><p className="text-xs text-slate-500">IFSC</p><p className="font-medium text-sm font-mono">{viewUserDetails.profile.bank_details.ifsc}</p></div>}
                          {viewUserDetails.profile.bank_details.account_number && <div className="col-span-2"><p className="text-xs text-slate-500">Account Number</p><p className="font-medium text-sm font-mono">{viewUserDetails.profile.bank_details.account_number}</p></div>}
                          {viewUserDetails.profile.bank_details.upi_id && <div className="col-span-2"><p className="text-xs text-slate-500">UPI ID</p><p className="font-medium text-sm font-mono">{viewUserDetails.profile.bank_details.upi_id}</p></div>}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic p-3 bg-slate-50 rounded-lg">No bank details submitted</p>
                    )}
                  </div>

                  {/* Approve/Reject action buttons (only for onboarded providers) */}
                  {viewUserDetails.profile.onboarding_completed && viewUserDetails.profile.approval_status !== 'approved' && (
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={() => { handleApproveProvider(viewUserDetails.user.id, true); setViewUserDetails(null); }}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="modal-approve-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" /> Approve Provider
                      </Button>
                      <Button
                        onClick={() => { handleApproveProvider(viewUserDetails.user.id, false); setViewUserDetails(null); }}
                        variant="outline"
                        className="flex-1 border-red-200 text-red-700 hover:bg-red-50"
                        data-testid="modal-reject-btn"
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* No profile fallback */}
              {!viewUserDetails.profile && viewUserDetails.user?.role !== 'admin' && (
                <p className="text-sm text-slate-500 italic">No additional profile data found for this user.</p>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setViewUserDetails(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
